/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

export const MAX_OBJECTS = 25;
export const MAX_FIELDS = 50;

export const FIELD_TYPES = [
  { value: 'text', label: 'Text', description: 'Up to 25 characters' },
  { value: 'long_text', label: 'Long Text', description: 'Rich text up to 15,000 characters' },
  { value: 'number', label: 'Number', description: 'Integer or decimal number' },
  { value: 'currency', label: 'Currency', description: 'Amount with ISO currency code' },
  { value: 'decimal', label: 'Decimal', description: 'Fixed decimal number' },
  { value: 'boolean', label: 'Boolean', description: 'Yes/No style choice with custom labels' },
  { value: 'checkbox', label: 'Checkbox', description: 'Single checkbox with custom labels' },
  { value: 'url', label: 'URL', description: 'Web address (http/https)' },
  { value: 'email', label: 'Email', description: 'Email address' },
  { value: 'date', label: 'Date', description: 'Calendar date' },
  { value: 'datetime', label: 'Date & Time', description: 'Date and time' },
  { value: 'relation', label: 'Relation', description: 'Link to one record' },
  { value: 'time', label: 'Time', description: 'Local time (no timezone)' },
  { value: 'dropdown', label: 'Dropdown', description: 'Single choice from options' },
  { value: 'select_list', label: 'Select List', description: 'Multiple choices from options' },
  { value: 'list_of_texts', label: 'List of Texts', description: 'Delimiter-separated text values' },
  { value: 'image', label: 'Image', description: 'Upload an image file (stored in cloud storage)' },
];

export const BOOLEAN_LABEL_PRESETS = [
  { trueLabel: 'Yes', falseLabel: 'No' },
  { trueLabel: 'True', falseLabel: 'False' },
  { trueLabel: 'Active', falseLabel: 'Inactive' },
  { trueLabel: 'On', falseLabel: 'Off' },
  { trueLabel: 'Enabled', falseLabel: 'Disabled' },
];

export const BOOLEAN_DISPLAY_OPTIONS = [
  { value: 'switch', label: 'Switch' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'dropdown', label: 'Dropdown (Yes/No labels)' },
];

export const RELATION_TARGETS = [
  { value: 'lead', label: 'Lead' },
  { value: 'deal', label: 'Deal' },
  { value: 'account', label: 'Account' },
  { value: 'contact', label: 'Contact' },
  { value: 'order', label: 'Order' },
  { value: 'quotation', label: 'Quotation' },
  { value: 'product', label: 'Product' },
  { value: 'case', label: 'Case' },
];

export const ISO_CURRENCIES = [
  { code: 'USD', name: 'US Dollar' },
  { code: 'EUR', name: 'Euro' },
  { code: 'AED', name: 'UAE Dirham' },
  { code: 'AUD', name: 'Australian Dollar' },
  { code: 'SGD', name: 'Singapore Dollar' },
  { code: 'CAD', name: 'Canadian Dollar' },
  { code: 'INR', name: 'Indian Rupee' },
  { code: 'GBP', name: 'British Pound' },
  { code: 'NZD', name: 'New Zealand Dollar' },
  { code: 'JPY', name: 'Japanese Yen' },
  { code: 'CHF', name: 'Swiss Franc' },
];

/**
 * Build the full list of selectable relation targets: standard CRM objects
 * plus every tenant Custom Object (prefixed with "co_" so the record
 * picker/renderer can tell them apart from standard objects at runtime).
 */
export function getRelationTargetOptions(customObjects = []) {
  const standard = RELATION_TARGETS.map((t) => ({ ...t, group: 'Standard Objects' }));
  const custom = (customObjects || [])
    .filter((obj) => obj?.apiName)
    .map((obj) => ({
      value: `co_${obj.apiName}`,
      label: obj.pluralName || obj.name || obj.apiName,
      group: 'Custom Entities',
    }));
  return [...standard, ...custom];
}

export function formatCurrencyOption(currency) {
  if (!currency) return '';
  if (typeof currency === 'string') return currency;
  return `${currency.code} - ${currency.name}`;
}

export const DEFAULT_SECTION = () => ({
  id: 'basic',
  name: 'Basic Information',
  isSystem: false,
  fields: [],
});

export const DEFAULT_FIELD = () => ({
  id: `field_${Date.now()}`,
  name: '',
  label: '',
  type: 'text',
  required: false,
  placeholder: '',
  helpText: '',
  maxLength: undefined,
  options: [],
  referencedObject: 'lead',
  delimiter: ',',
  decimalPlaces: 2,
  currencyCode: 'USD',
  trueLabel: 'Yes',
  falseLabel: 'No',
  booleanDisplay: 'switch',
  _nameLocked: false,
});

const FIELD_NAME_PATTERN = /^[a-z_][a-z0-9_]*$/;

export function slugifyFieldName(label) {
  return (label || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_+/g, '_')
    .slice(0, 40);
}

export function validateFieldDefinition(field) {
  const errors = [];
  if (!field.label?.trim()) errors.push('Field label is required');
  if (!field.name?.trim() || !FIELD_NAME_PATTERN.test(field.name)) {
    errors.push('Field API name must use lowercase letters, numbers, and underscores');
  }
  if (['dropdown', 'select_list'].includes(field.type) && !(field.options || []).filter(Boolean).length) {
    errors.push(`${field.label || field.name}: at least one option is required`);
  }
  if (['relation', 'multirelation'].includes(field.type) && !field.referencedObject) {
    errors.push(`${field.label || field.name}: related object is required`);
  }
  return errors;
}

export function validateObjectDefinition(objectDef) {
  const errors = [];
  if (!objectDef.name?.trim()) errors.push('Object name is required');
  if (!objectDef.pluralName?.trim()) errors.push('Plural name is required');
  let fieldCount = 0;
  (objectDef.sections || []).forEach((section) => {
    if (section.isSystem) return;
    (section.fields || []).forEach((field) => {
      fieldCount += 1;
      errors.push(...validateFieldDefinition(field));
    });
  });
  if (fieldCount > MAX_FIELDS) errors.push(`Maximum ${MAX_FIELDS} custom fields allowed`);
  return errors;
}

export function countCustomFields(sections) {
  return (sections || []).reduce((total, section) => {
    if (section.isSystem) return total;
    return total + (section.fields || []).length;
  }, 0);
}

export function buildPayload(objectDef) {
  return {
    name: objectDef.name.trim(),
    pluralName: objectDef.pluralName.trim(),
    description: objectDef.description || '',
    sections: (objectDef.sections || [])
      .filter((s) => !s.isSystem && s.id !== 'audit')
      .map((section) => ({
        id: section.id,
        name: section.name,
        isSystem: false,
        fields: (section.fields || []).map((field) => ({
          id: field.id,
          name: field.name,
          label: field.label,
          type: field.type,
          required: !!field.required,
          placeholder: field.placeholder || undefined,
          helpText: field.helpText || undefined,
          maxLength: field.maxLength != null ? Number(field.maxLength) : undefined,
          options: field.options?.filter(Boolean),
          referencedObject: field.referencedObject,
          displayField: field.displayField,
          decimalPlaces: field.decimalPlaces,
          currencyCode: field.currencyCode,
          delimiter: field.delimiter,
          maxItems: field.maxItems,
          maxItemLength: field.maxItemLength,
          minValue: field.minValue,
          maxValue: field.maxValue,
          trueLabel: field.trueLabel,
          falseLabel: field.falseLabel,
          booleanDisplay: field.booleanDisplay,
        })),
      })),
  };
}

export function formatCellValue(field, value) {
  if (value === null || value === undefined || value === '') return '—';
  switch (field.type) {
    case 'boolean':
    case 'checkbox': {
      const trueLabel = field.trueLabel || 'Yes';
      const falseLabel = field.falseLabel || 'No';
      return value ? trueLabel : falseLabel;
    }
    case 'currency':
      if (typeof value === 'object') return `${value.currencyCode || ''} ${value.amount ?? ''}`.trim();
      try {
        const parsed = JSON.parse(value);
        return `${parsed.currencyCode || ''} ${parsed.amount ?? ''}`.trim();
      } catch {
        return String(value);
      }
    case 'long_text':
      return String(value).replace(/<[^>]+>/g, ' ').trim().slice(0, 80);
    case 'url': {
      const url = String(value);
      return url.length > 48 ? `${url.slice(0, 45)}…` : url;
    }
    case 'image': {
      if (!value) return '—';
      if (typeof value === 'string') {
        return value.length > 48 ? `${value.slice(0, 45)}…` : value;
      }
      if (typeof value === 'object') {
        return value.file_name || value.storage_path || value.url || 'Image';
      }
      return String(value);
    }
    case 'multirelation':
    case 'select_list':
      return Array.isArray(value) ? value.join(', ') : String(value);
    case 'list_of_texts':
      return Array.isArray(value) ? value.join(', ') : String(value);
    default: {
      const text = String(value);
      // Truncate UUID-like / very long plain values for list density
      if (text.length > 48) return `${text.slice(0, 45)}…`;
      return text;
    }
  }
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isEmptyValue(value) {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string' && !value.trim()) return true;
  if (Array.isArray(value) && value.length === 0) return true;
  if (typeof value === 'object' && !Array.isArray(value) && value.amount === null && !value.amount) {
    if (value.amount === '' || value.amount == null) return true;
  }
  return false;
}

/** Validate record form values before create/update. Returns { fieldErrors, messages }. */
export function validateRecordValues(fields, formData) {
  const fieldErrors = {};
  const messages = [];

  (fields || []).forEach((field) => {
    if (field.readOnly) return;
    const label = field.label || field.name;
    const value = formData?.[field.name];

    if (field.required && isEmptyValue(value)) {
      const msg = `${label} is required`;
      fieldErrors[field.name] = msg;
      messages.push(msg);
      return;
    }

    if (isEmptyValue(value)) return;

    if (field.type === 'email') {
      const email = String(value).trim();
      if (!EMAIL_PATTERN.test(email)) {
        const msg = `${label}: enter a valid email (e.g. name@example.com)`;
        fieldErrors[field.name] = msg;
        messages.push(msg);
      }
      return;
    }

    if (field.type === 'url') {
      let url = String(value).trim();
      if (url && !url.includes('://')) url = `https://${url}`;
      try {
        const parsed = new URL(url);
        if (!['http:', 'https:'].includes(parsed.protocol) || !parsed.hostname) {
          throw new Error('bad');
        }
        if (!parsed.hostname.includes('.') && parsed.hostname !== 'localhost') {
          throw new Error('bad');
        }
      } catch {
        const msg = `${label}: enter a valid URL (e.g. https://example.com)`;
        fieldErrors[field.name] = msg;
        messages.push(msg);
      }
      return;
    }

    if (field.type === 'image') {
      const images = Array.isArray(value) ? value : [value];
      const validImages = images.filter((meta) => {
        if (!meta) return false;
        if (typeof meta === 'string') {
          const text = meta.trim();
          return text.startsWith('http://') || text.startsWith('https://') || text.startsWith('{');
        }
        if (typeof meta !== 'object') return false;
        return (
          (
            (meta.bucket_name || meta.bucket) &&
            (meta.storage_path || meta.path)
          ) ||
          (typeof meta.url === 'string' && meta.url.trim()) ||
          (meta.is_local_preview && meta.file)
        );
      });
      if (!validImages.length || validImages.length !== images.length) {
        const msg = `${label}: upload an image`;
        fieldErrors[field.name] = msg;
        messages.push(msg);
      }
      return;
    }

    if (field.type === 'text' && String(value).length > (field.maxLength || 25)) {
      const msg = `${label}: must be at most ${field.maxLength || 25} characters`;
      fieldErrors[field.name] = msg;
      messages.push(msg);
    }
  });

  return { fieldErrors, messages, isValid: messages.length === 0 };
}
